import React, { useState, useEffect, useRef } from 'react';
import { Bot, X, Send, Compass, ArrowRight, Zap, HelpCircle, FileText, Upload, Edit3, CheckCircle, ChevronDown, Phone, MessageCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDarkMode } from '../DarkModeContext';
import { useTenant } from '../config/tenantConfig';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

/**
 * Referral Guide Agent
 * AI-powered assistant that guides referral agents through the onboarding process
 * Context-aware - adapts guidance based on current page/step
 */
const ReferralGuideAgent = ({
  initiallyOpen = false,
  currentStep = 'choice', // 'choice', 'manual', 'automatic', 'review', 'complete'
  onNavigate = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useDarkMode();
  const { theme } = useTenant();
  const { user } = useAuth();

  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [hasGreeted, setHasGreeted] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Detect current step from URL
  const detectCurrentStep = () => {
    const path = location.pathname;
    if (path.includes('onboarding-choice')) return 'choice';
    if (path.includes('invoice-onboarding/manual')) return 'manual';
    if (path.includes('invoice-onboarding/automatic')) return 'automatic';
    if (path.includes('invoice-onboarding')) return 'choice';
    if (path.includes('my-submissions')) return 'submissions';
    if (path.includes('dashboard')) return 'dashboard';
    return currentStep;
  };

  const activeStep = detectCurrentStep();

  // Welcome message based on current step
  const getWelcomeMessage = () => {
    const userName = user?.first_name || user?.firstName || 'there';

    switch (activeStep) {
      case 'choice':
        return `👋 **Hi ${userName}! Ready to submit a new referral?**

I'm your AI guide, here to help you submit your client's financing opportunity.

**You have two options:**

📝 **Manual Entry** - Enter your client's details step by step
   - Best when you have supplier & buyer info ready
   - Full control over all fields

📄 **AI Onboarding Studio** - Upload your client's invoice
   - Fastest way to onboard clients
   - Auto-creates supplier & buyer accounts
   - Instant eligibility assessment

**Which would you like to try?** Or ask me any questions! 🚀`;

      case 'manual':
        return `📝 **Manual Entry Mode**

Great choice! I'll guide you through entering your client's deal information.

**Key sections to complete:**
1. **Supplier Information** - Your client's supplier (seller)
2. **Buyer Information** - Your client's buyer (customer)
3. **Transaction Details** - Invoice amount, dates, terms
4. **Supporting Documents** - Upload any docs you have

**Tips:**
• Required fields are marked with *
• You can save and continue later
• I'm here if you need help with any field!

What would you like to know? 📋`;

      case 'automatic':
        return `📄 **AI Onboarding Studio**

Excellent! This is the fastest way to onboard your client.

**How it works:**
1. **Upload Invoice** - Your client's commercial invoice
2. **AI Extraction** - I'll extract supplier & buyer info
3. **Auto-Create Accounts** - Supplier & buyer accounts created
4. **Eligibility Check** - Instant assessment of financing eligibility

**What happens next:**
• Supplier & buyer accounts are created automatically
• The system evaluates if the deal is eligible for financing
• You can review and submit for final approval

**Ready to upload?** Just drag & drop your client's invoice! 📁`;

      case 'submissions':
        return `📋 **Your Referral Submissions**

Here you can track all the client referrals you've submitted.

**Status meanings:**
• 🟡 **Pending** - Under review by the platform
• 🟢 **Approved** - Ready for financing
• 🔴 **Rejected** - See notes for reasons
• 🔵 **In Progress** - Additional info needed

Need help with a specific referral? Just ask! 📊`;

      case 'dashboard':
        return `🏠 **Welcome to your Agent Dashboard!**

From here you can:
• Submit new client referrals
• Track your submitted referrals
• View your wallet & commissions
• Access your termsheets

**Quick actions:**
• Click "Submit Opportunity" to refer a new client
• Check "My Submissions" to track referral status

How can I help you today? 💼`;

      default:
        return `👋 **Hi ${userName}!**

I'm your AI guide for submitting client referrals.

How can I help you today? 🎯`;
    }
  };

  // Show welcome message when opened
  useEffect(() => {
    if (isOpen && !hasGreeted) {
      addAgentMessage(getWelcomeMessage());
      setHasGreeted(true);
    }
  }, [isOpen, hasGreeted, activeStep]);

  // Reset greeting when step changes
  useEffect(() => {
    setHasGreeted(false);
    setMessages([]);
  }, [activeStep]);

  const addAgentMessage = (text, actions = null) => {
    setMessages(prev => [...prev, {
      id: `agent_${Date.now()}`,
      type: 'agent',
      text,
      actions,
      timestamp: new Date()
    }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      id: `user_${Date.now()}`,
      type: 'user',
      text,
      timestamp: new Date()
    }]);
  };

  // Quick actions based on current step
  const getQuickActions = () => {
    switch (activeStep) {
      case 'choice':
        return [
          { label: 'Start Manual Entry', icon: Edit3, action: () => handleNavigate('manual') },
          { label: 'Upload Invoice (AI)', icon: Upload, action: () => handleNavigate('automatic') },
          { label: 'How does eligibility work?', icon: HelpCircle, action: () => handleQuickQuestion('How does the eligibility assessment work?') }
        ];
      case 'manual':
        return [
          { label: 'Help with Supplier Info', icon: HelpCircle, action: () => handleQuickQuestion('What supplier information do I need?') },
          { label: 'Help with Buyer Info', icon: HelpCircle, action: () => handleQuickQuestion('What buyer information do I need?') },
          { label: 'Switch to AI Studio', icon: Upload, action: () => handleNavigate('automatic') }
        ];
      case 'automatic':
        return [
          { label: 'What info is extracted?', icon: HelpCircle, action: () => handleQuickQuestion('What information is extracted from the invoice?') },
          { label: 'How is eligibility assessed?', icon: Zap, action: () => handleQuickQuestion('How does the eligibility assessment work?') },
          { label: 'Switch to Manual', icon: Edit3, action: () => handleNavigate('manual') }
        ];
      case 'submissions':
        return [
          { label: 'Submit New Opportunity', icon: FileText, action: () => handleNavigate('choice') },
          { label: 'What do statuses mean?', icon: HelpCircle, action: () => handleQuickQuestion('What do the different submission statuses mean?') }
        ];
      default:
        return [
          { label: 'Submit Opportunity', icon: FileText, action: () => handleNavigate('choice') },
          { label: 'View Submissions', icon: CheckCircle, action: () => handleNavigate('submissions') },
          { label: 'Speak with Sales', icon: HelpCircle, action: () => handleQuickQuestion('I want to speak with sales, what is the next step?') }
        ];
    }
  };

  const handleNavigate = (destination) => {
    const paths = {
      choice: '/halitrade/onboarding-choice',
      manual: '/halitrade/invoice-onboarding/manual',
      automatic: '/halitrade/invoice-onboarding/automatic',
      submissions: '/halitrade/my-submissions',
      dashboard: '/halitrade/dashboard'
    };

    if (onNavigate) {
      onNavigate(destination);
    } else {
      navigate(paths[destination] || paths.choice);
    }
    setIsOpen(false);
  };

  // Open Contact Center (SupportChatAgent)
  const openContactCenter = () => {
    console.log('[ReferralGuideAgent] Dispatching supportChat:open event');
    window.dispatchEvent(new Event('supportChat:open'));
    setIsOpen(false);
  };

  // Open Contact Center directly to Live Chat
  const openLiveChat = () => {
    console.log('[ReferralGuideAgent] Dispatching supportChat:openLiveChat event');
    window.dispatchEvent(new CustomEvent('supportChat:openLiveChat'));
    setIsOpen(false);
  };

  const handleQuickQuestion = (question) => {
    setUserInput(question);
    setTimeout(() => handleSendMessage(question), 100);
  };

  const handleSendMessage = async (messageOverride = null) => {
    const messageText = messageOverride || userInput.trim();
    if (!messageText || isTyping) return;

    addUserMessage(messageText);
    setUserInput('');
    setIsTyping(true);

    // Check if user wants to speak with sales/support - show Contact Center buttons directly
    const lowerMessage = messageText.toLowerCase();
    if (lowerMessage.includes('sales') || lowerMessage.includes('speak') || lowerMessage.includes('talk') || lowerMessage.includes('human') || lowerMessage.includes('support') || lowerMessage.includes('contact')) {
      const contactResponse = `📞 **Contact Center - Need to speak with someone?**

**Available Support Options:**

1. **🤖 AI Chat Support**
   Get instant answers 24/7 from our AI assistant

2. **💬 Live Chat Support**
   Connect with our support team in real-time

3. **📧 Email Us**
   sales@halisoft.com - Partnership & contract inquiries
   support@halisoft.com - Technical support

**What can our team help with?**
• Setting up your agent contract/termsheet
• Commission rate discussions
• Technical issues with submissions
• Questions about specific deals
• Partnership opportunities`;

      // Add message with action buttons for Contact Center
      addAgentMessage(contactResponse, [
        { label: 'Open Contact Center', icon: MessageCircle, action: openContactCenter },
        { label: 'Start Live Chat', icon: Phone, action: openLiveChat }
      ]);
      setIsTyping(false);
      return;
    }

    try {
      // Build context for AI
      const context = {
        currentStep: activeStep,
        currentPage: location.pathname,
        userName: user?.first_name || user?.firstName || 'Agent',
        userRole: 'referral_agent',
        agentCompany: user?.company_name || user?.companyName || null,
        hasContract: user?.has_termsheet || user?.hasTermsheet || false
      };

      const systemContext = `You are a helpful AI guide assisting a referral agent through the HaliAgent submission portal.

CURRENT CONTEXT:
- Page: ${activeStep}
- User: ${context.userName}
- Agent Company: ${context.agentCompany || 'Not specified'}
- Has Contract/Termsheet: ${context.hasContract ? 'Yes' : 'Unknown'}
- Role: Referral Agent (Business Partner)

=== REFERRAL AGENT BUSINESS MODEL ===

**What is a Referral Agent?**
A Referral Agent (also called Business Partner) is a professional who sources and refers trade finance opportunities to the platform. They earn commissions on successfully financed deals.

**Agent Contract / Termsheet:**
- Before submitting referrals, agents should have a signed contract/termsheet with the platform
- The termsheet defines: commission rates, payment terms, territory rights, exclusivity (if any)
- Agents can view their termsheet in "My Termsheets" section
- If no contract exists, the agent should contact support to set one up

**Commission Structure (typical):**
- Commission is paid on successfully financed invoices
- Rate varies based on deal volume, client quality, and contract terms
- Commissions are tracked in the Agent Wallet
- Payments are made according to termsheet schedule

**Referral Registration Process:**
1. Agent identifies a client needing invoice financing
2. Agent collects client's invoice (and optionally other trade documents)
3. Agent submits the referral through Manual Entry or AI Studio
4. Platform evaluates supplier & buyer creditworthiness
5. If eligible, financing is offered to the client
6. Agent earns commission when invoice is financed

=== THE REFERRAL SUBMISSION WORKFLOW ===

1. **Onboarding Choice** (/halitrade/onboarding-choice)
   - Choose between Manual Entry or AI Onboarding Studio

2. **Manual Entry** (/halitrade/invoice-onboarding/manual)
   - Enter your client's supplier and buyer information
   - Add transaction details (invoice amount, dates, terms)
   - Upload supporting documents

3. **AI Onboarding Studio** (/halitrade/invoice-onboarding/automatic)
   - Upload your client's commercial invoice
   - AI extracts supplier & buyer information automatically
   - System auto-creates supplier & buyer accounts
   - Instant eligibility assessment
   - Review and submit for approval

4. **My Submissions** (/halitrade/my-submissions)
   - Track all your referral submissions
   - View status: Pending, Approved, Rejected, In Progress
   - Check notes and feedback from the platform

=== ELIGIBILITY ASSESSMENT ===

**What makes a deal eligible?**
- Supplier has verifiable business registration
- Buyer has good payment history / creditworthiness
- Invoice is valid and not already financed elsewhere
- Trade terms are within acceptable parameters
- Countries involved are not sanctioned

**What happens after submission?**
1. Platform reviews the referral
2. Credit checks on supplier and buyer
3. Document verification
4. Financing decision communicated
5. If approved, client receives financing offer
6. Agent earns commission on funded deals

=== KEY INFORMATION TO COLLECT ===

**From the Invoice:**
- Invoice number (critical!)
- Invoice date and due date
- Supplier name, address, country
- Buyer name, address, country
- Total amount and currency
- Payment terms
- Goods description

**Tips for Success:**
- Ensure invoice is clear and readable
- Complete supplier/buyer details increase approval chances
- Country information is critical for compliance
- Upload the best quality document you have

=== CONTACT & SUPPORT ===

**If the agent wants to speak with sales or needs human support:**
- Live Chat: Available through the support chat icon
- Email: sales@halisoft.com (partnerships), support@halisoft.com (technical)
- Schedule a call: Through the website

**What sales/support can help with:**
- Setting up agent contracts/termsheets
- Commission rate negotiations
- Technical issues with submissions
- Questions about specific deals
- Partnership opportunities

USER QUESTION: ${messageText}

Provide helpful, concise guidance. Be friendly and professional.
- If they ask about contract/termsheet → check "My Termsheets" or contact support
- If they want to speak with sales/support → provide contact options and offer to open live chat`;

      const response = await api.post('/api/chat/chat', {
        message: systemContext,
        conversationId: conversationId,
        context: context
      });

      if (response.data.conversationId && !conversationId) {
        setConversationId(response.data.conversationId);
      }

      const aiResponse = response.data.response || response.data.message || response.data.reply;

      if (aiResponse) {
        addAgentMessage(aiResponse);
      } else {
        throw new Error('No response');
      }

    } catch (error) {
      console.error('[ReferralGuideAgent] Error:', error);

      // Fallback responses based on common questions
      const lowerMessage = messageText.toLowerCase();
      let fallbackResponse = "I'm having trouble connecting right now. Here's what I can tell you:";

      if (lowerMessage.includes('document') || lowerMessage.includes('upload')) {
        fallbackResponse = `📄 **Documents you can upload:**

• **Commercial Invoice** (required) - Shows the sale details
• **Purchase Order** - Buyer's order confirmation
• **Bill of Lading** - Shipping/transport document
• **Financial Statements** - For credit analysis

**Tips for best results:**
• Use clear, readable PDF or images
• Ensure all text is visible
• Multi-page documents are supported`;
      } else if (lowerMessage.includes('supplier') || lowerMessage.includes('seller')) {
        fallbackResponse = `🏢 **Supplier Information needed:**

• Company name
• Full address
• Country (important!)
• Contact person
• Email and phone
• Tax ID / Registration number

This is the company selling the goods.`;
      } else if (lowerMessage.includes('buyer') || lowerMessage.includes('customer')) {
        fallbackResponse = `🛒 **Buyer Information needed:**

• Company name
• Full address
• Country (important!)
• Contact person
• Email and phone
• Tax ID / Registration number

This is the company purchasing the goods.`;
      } else if (lowerMessage.includes('status') || lowerMessage.includes('submission')) {
        fallbackResponse = `📊 **Submission Statuses:**

• 🟡 **Pending** - Under review
• 🟢 **Approved** - Ready for financing
• 🔴 **Rejected** - Check notes for details
• 🔵 **In Progress** - Needs more info

You can track all submissions in "My Submissions".`;
      } else if (lowerMessage.includes('contract') || lowerMessage.includes('termsheet') || lowerMessage.includes('commission')) {
        fallbackResponse = `📋 **Your Agent Contract & Commissions:**

**Agent Termsheet:**
• Your termsheet defines your commission rates and payment terms
• View your contract in "My Termsheets" section
• If you don't have a contract yet, please contact support

**Commission Structure:**
• Commissions are earned on successfully financed invoices
• Rates depend on your contract terms
• Track earnings in your Agent Wallet
• Payments follow the schedule in your termsheet

**Need help?** Contact support to discuss your contract terms.`;
      } else if (lowerMessage.includes('eligib') || lowerMessage.includes('assess')) {
        fallbackResponse = `✅ **Eligibility Assessment:**

**What makes a deal eligible?**
• Supplier has verifiable business registration
• Buyer has good creditworthiness
• Invoice is valid and not already financed
• Trade terms are acceptable
• No sanctioned countries involved

**After submission:**
1. Platform reviews your referral
2. Credit checks on supplier & buyer
3. Document verification
4. Decision communicated to you
5. Client receives financing offer if approved
6. You earn commission on funded deals`;
      } else if (lowerMessage.includes('sales') || lowerMessage.includes('speak') || lowerMessage.includes('talk') || lowerMessage.includes('human') || lowerMessage.includes('support') || lowerMessage.includes('contact') || lowerMessage.includes('help')) {
        fallbackResponse = `📞 **Contact Center - Need to speak with someone?**

**Available Support Options:**

1. **🤖 AI Chat Support**
   Get instant answers 24/7 from our AI assistant

2. **💬 Live Chat Support**
   Connect with our support team in real-time

3. **📧 Email Us**
   sales@halisoft.com - Partnership & contract inquiries
   support@halisoft.com - Technical support

**What can our team help with?**
• Setting up your agent contract/termsheet
• Commission rate discussions
• Technical issues with submissions
• Questions about specific deals
• Partnership opportunities`;

        // Add message with action buttons for Contact Center
        addAgentMessage(fallbackResponse, [
          { label: 'Open Contact Center', icon: MessageCircle, action: openContactCenter },
          { label: 'Start Live Chat', icon: Phone, action: openLiveChat }
        ]);
        return;
      }

      addAgentMessage(fallbackResponse);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Primary color based on theme (yellow for HaliAgent)
  const primaryColor = theme?.primary || '#F6B800';

  return (
    <>
      {/* Floating Guide Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 text-white font-semibold"
          style={{ backgroundColor: primaryColor }}
        >
          <Bot className="w-5 h-5" />
          <span>Need Help?</span>
          <Zap className="w-4 h-4 opacity-70" />
        </button>
      )}

      {/* Guide Panel */}
      {isOpen && (
        <div className={`fixed bottom-0 right-0 left-0 md:bottom-6 md:right-6 md:left-auto w-full md:w-96 max-h-[90vh] md:max-h-[80vh] z-50 flex flex-col rounded-t-2xl md:rounded-2xl shadow-2xl overflow-hidden ${
          darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>

          {/* Header */}
          <div
            className="px-5 py-4 flex items-center justify-between text-white"
            style={{ backgroundColor: primaryColor }}
          >
            <div className="flex items-center gap-3">
              <Bot className="w-6 h-6" />
              <div>
                <h3 className="font-bold text-base">Submission Guide</h3>
                <p className="text-white/80 text-xs">I'm here to help you! 🎯</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Actions */}
          <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => setShowQuickActions(!showQuickActions)}
              className={`w-full px-4 py-2 flex items-center justify-between ${
                darkMode ? 'bg-gray-800/50 hover:bg-gray-800' : 'bg-gray-50 hover:bg-gray-100'
              } transition-colors`}
            >
              <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Quick Actions
              </span>
              <ChevronDown
                size={14}
                className={`transition-transform ${showQuickActions ? 'rotate-180' : ''} ${
                  darkMode ? 'text-gray-400' : 'text-gray-600'
                }`}
              />
            </button>

            {showQuickActions && (
              <div className={`px-4 py-3 space-y-2 ${darkMode ? 'bg-gray-800/30' : 'bg-gray-50/50'}`}>
                {getQuickActions().map((action, idx) => (
                  <button
                    key={idx}
                    onClick={action.action}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
                    }`}
                  >
                    <action.icon size={16} style={{ color: primaryColor }} />
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Messages */}
          <div className={`flex-1 overflow-y-auto px-4 py-3 space-y-3 ${
            darkMode ? 'bg-gray-900' : 'bg-gray-50'
          }`} style={{ minHeight: '250px' }}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${message.type === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`px-4 py-3 rounded-2xl ${
                    message.type === 'user'
                      ? 'text-white'
                      : darkMode
                        ? 'bg-gray-800 text-gray-200'
                        : 'bg-white text-gray-800 border border-gray-200'
                  }`}
                  style={message.type === 'user' ? { backgroundColor: primaryColor } : {}}
                  >
                    {message.type === 'agent' && (
                      <div className="flex items-center gap-2 mb-2">
                        <Bot size={14} style={{ color: primaryColor }} />
                        <span className="text-xs font-semibold" style={{ color: primaryColor }}>
                          AI Guide
                        </span>
                      </div>
                    )}
                    <div className="text-sm whitespace-pre-wrap break-words">
                      {message.text}
                    </div>
                    {/* Action Buttons (if any) */}
                    {message.actions && message.actions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                        {message.actions.map((action, idx) => (
                          <button
                            key={idx}
                            onClick={action.action}
                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                              idx === 0
                                ? 'text-white hover:opacity-90'
                                : darkMode
                                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                            style={idx === 0 ? { backgroundColor: primaryColor } : {}}
                          >
                            <action.icon size={16} />
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={`text-xs mt-1 ${
                    message.type === 'user' ? 'text-right' : ''
                  } ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className={`px-4 py-3 rounded-2xl ${
                  darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Bot size={14} style={{ color: primaryColor }} />
                    <span className="text-xs font-semibold" style={{ color: primaryColor }}>
                      AI Guide
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`flex-shrink-0 px-4 py-3 border-t ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex gap-2">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isTyping}
                placeholder={isTyping ? "Thinking..." : "Ask me anything..."}
                className={`flex-1 px-4 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 ${
                  darkMode
                    ? 'bg-gray-700 text-white placeholder-gray-400 border border-gray-600 focus:ring-yellow-500/50'
                    : 'bg-gray-100 text-gray-900 placeholder-gray-500 border border-gray-300 focus:ring-yellow-500/50'
                }`}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!userInput.trim() || isTyping}
                className={`px-4 py-2 rounded-xl transition-all duration-200 text-white ${
                  userInput.trim() && !isTyping
                    ? 'hover:opacity-90'
                    : 'opacity-50 cursor-not-allowed'
                }`}
                style={{ backgroundColor: primaryColor }}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReferralGuideAgent;
